import { DateTime } from 'luxon'

export const DASHBOARD_DATE_RANGE_STORAGE_KEY = 'dashboardAdvancedFilters'
export const DASHBOARD_DATE_RANGE_STORAGE_VERSION = 4

const getDashboardStorageDate = () => DateTime.now()
  .setZone('America/Mexico_City')
  .toFormat('yyyy-MM-dd')

export const DATE_FILTER_TYPES = [
  { value: 'custom', label: 'Date range' },
  { value: 'day', label: 'Day by day' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' }
]

export const DATE_PRESETS_BY_TYPE = {
  custom: [{ value: 'custom', label: 'Custom range' }],
  day: [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_workday', label: 'Last workday', backendResolved: true },
    { value: 'previous_workday', label: 'Previous workday', backendResolved: true },
    { value: 'last_harvest_day', label: 'Last harvest day', backendResolved: true },
    { value: 'previous_harvest_day', label: 'Previous harvest day', backendResolved: true },
    { value: 'last_agro_day', label: 'Last agro day', backendResolved: true },
    { value: 'previous_agro_day', label: 'Previous agro day', backendResolved: true }
  ],
  week: [
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'week_number', label: 'Week number' },
    { value: 'current_week', label: 'Current week' },
    { value: 'previous_week', label: 'Previous week' }
  ],
  month: [
    { value: 'current_month', label: 'Current month' },
    { value: 'previous_month', label: 'Previous month' },
    { value: 'last_30_days', label: 'Last 30 days' }
  ],
  year: [
    { value: 'current_season', label: 'Current season', backendResolved: true },
    { value: 'previous_season', label: 'Previous season', backendResolved: true },
    { value: 'last_12_months', label: 'Last 12 months' }
  ]
}

export const createDefaultDateRange = (date) => ({
  dateFrom: date,
  dateTo: date,
  dateFilterType: 'custom',
  datePreset: 'custom',
  weekNumber: String(DateTime.fromISO(date).weekNumber),
  weekYear: String(DateTime.fromISO(date).weekYear),
  sectorId: '',
  groupId: '',
  activityId: '',
  blockGroupName: '',
  deviceGroupName: '',
  activityName: ''
})

const toIsoStartDateTime = (date) => {
  if (!date) return ''
  return new Date(date).toISOString()
}

const toIsoEndDateTime = (date) => {
  if (!date) return ''
  return new Date(`${date}T23:59:59.999Z`).toISOString()
}

const toDateString = dateTime => dateTime.toFormat('yyyy-MM-dd')

const mergeRange = (range, dateFrom, dateTo) => ({
  ...range,
  dateFrom,
  dateTo
})

const backendResolvedPresets = new Set(
  Object.values(DATE_PRESETS_BY_TYPE)
    .flat()
    .filter(preset => preset.backendResolved)
    .map(preset => preset.value)
)

export const resolveDashboardDateRangeFilter = (range, today = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')) => {
  const now = DateTime.fromISO(today).setZone('America/Mexico_City')
  const dateFilterType = range.dateFilterType || 'custom'
  const datePreset = range.datePreset || 'custom'

  if (dateFilterType === 'custom') return range
  if (backendResolvedPresets.has(datePreset)) return { ...range, dateFrom: '', dateTo: '' }

  if (dateFilterType === 'day') {
    if (datePreset === 'yesterday') {
      const yesterday = toDateString(now.minus({ days: 1 }))
      return mergeRange(range, yesterday, yesterday)
    }

    return mergeRange(range, toDateString(now), toDateString(now))
  }

  if (dateFilterType === 'week') {
    if (datePreset === 'last_7_days') {
      return mergeRange(range, toDateString(now.minus({ days: 6 })), toDateString(now))
    }

    if (datePreset === 'previous_week') {
      const previousWeek = now.minus({ weeks: 1 })
      return mergeRange(range, toDateString(previousWeek.startOf('week')), toDateString(previousWeek.endOf('week')))
    }

    if (datePreset === 'week_number') {
      const weekYear = Number(range.weekYear || now.weekYear)
      const weekNumber = Number(range.weekNumber || now.weekNumber)
      const firstWeekDay = DateTime.fromObject({ weekYear, weekNumber, weekday: 1 })
      return mergeRange(range, toDateString(firstWeekDay), toDateString(firstWeekDay.plus({ days: 6 })))
    }

    return mergeRange(range, toDateString(now.startOf('week')), toDateString(now.endOf('week')))
  }

  if (dateFilterType === 'month') {
    if (datePreset === 'previous_month') {
      const previousMonth = now.minus({ months: 1 })
      return mergeRange(range, toDateString(previousMonth.startOf('month')), toDateString(previousMonth.endOf('month')))
    }

    if (datePreset === 'last_30_days') {
      return mergeRange(range, toDateString(now.minus({ days: 29 })), toDateString(now))
    }

    return mergeRange(range, toDateString(now.startOf('month')), toDateString(now.endOf('month')))
  }

  if (dateFilterType === 'year' && datePreset === 'last_12_months') {
    return mergeRange(range, toDateString(now.minus({ months: 12 }).plus({ days: 1 })), toDateString(now))
  }

  return range
}

export const buildDashboardDateRangeQuery = (range) => {
  const resolvedRange = resolveDashboardDateRangeFilter(range)
  const params = new URLSearchParams()
  const dateFromIso = toIsoStartDateTime(resolvedRange.dateFrom)
  const dateToIso = toIsoEndDateTime(resolvedRange.dateTo)

  if (dateFromIso) params.set('dateFrom', dateFromIso)

  if (dateToIso) params.set('dateTo', dateToIso)
  if (resolvedRange.dateFilterType && resolvedRange.dateFilterType !== 'custom') params.set('dateFilterType', resolvedRange.dateFilterType)
  if (resolvedRange.datePreset && resolvedRange.datePreset !== 'custom') params.set('datePreset', resolvedRange.datePreset)
  if (resolvedRange.weekNumber && resolvedRange.datePreset === 'week_number') params.set('weekNumber', resolvedRange.weekNumber)
  if (resolvedRange.weekYear && resolvedRange.datePreset === 'week_number') params.set('weekYear', resolvedRange.weekYear)
  if (resolvedRange.sectorId) params.set('blockGroupId', resolvedRange.sectorId)
  if (resolvedRange.groupId) params.set('deviceGroupId', resolvedRange.groupId)
  if (resolvedRange.activityId) params.set('activityId', resolvedRange.activityId)

  return params.toString()
}

const getPresetLabel = (dateFilterType, datePreset) => {
  const preset = (DATE_PRESETS_BY_TYPE[dateFilterType] || [])
    .find(option => option.value === datePreset)
  return preset?.label || ''
}

const getFilterSummaryLabel = (range, idKey, labelKey, fallbackLabel) => {
  if (!range[idKey]) return ''
  return range[labelKey] || fallbackLabel
}

export const getDateRangeSummary = (range, today) => {
  const resolvedRange = resolveDashboardDateRangeFilter(range, today)
  const presetLabel = getPresetLabel(range.dateFilterType, range.datePreset)
  const dateSummary = resolvedRange.dateFrom && resolvedRange.dateTo
    ? resolvedRange.dateFrom === resolvedRange.dateTo ? resolvedRange.dateFrom : `${resolvedRange.dateFrom} a ${resolvedRange.dateTo}`
    : ''

  if (!dateSummary && presetLabel) {
    const summary = [presetLabel]
    const filterLabels = [
      getFilterSummaryLabel(range, 'sectorId', 'blockGroupName', 'Group'),
      getFilterSummaryLabel(range, 'groupId', 'deviceGroupName', 'Device group'),
      getFilterSummaryLabel(range, 'activityId', 'activityName', 'Activity')
    ].filter(Boolean)
    summary.push(...filterLabels)
    return summary.join(' · ')
  }

  if (!dateSummary) return ''

  const summary = [presetLabel && range.dateFilterType !== 'custom' ? `${presetLabel} (${dateSummary})` : dateSummary]

  const filterLabels = [
    getFilterSummaryLabel(range, 'sectorId', 'blockGroupName', 'Group'),
    getFilterSummaryLabel(range, 'groupId', 'deviceGroupName', 'Device group'),
    getFilterSummaryLabel(range, 'activityId', 'activityName', 'Activity')
  ].filter(Boolean)
  summary.push(...filterLabels)

  return summary.join(' · ')
}

const isValidStoredRange = (value) => (
  value &&
  typeof value === 'object' &&
  typeof value.dateFrom === 'string' &&
  typeof value.dateTo === 'string'
)

export const readStoredDashboardDateRange = (defaultDate, storage = localStorage) => {
  try {
    const storedValue = storage?.getItem(DASHBOARD_DATE_RANGE_STORAGE_KEY)
    if (!storedValue) return createDefaultDateRange(defaultDate)

    const parsedValue = JSON.parse(storedValue)
    if (!isValidStoredRange(parsedValue)) return createDefaultDateRange(defaultDate)
    if (parsedValue.storageVersion !== DASHBOARD_DATE_RANGE_STORAGE_VERSION) return createDefaultDateRange(defaultDate)
    if (parsedValue.storedAtDate !== defaultDate) {
      storage?.removeItem?.(DASHBOARD_DATE_RANGE_STORAGE_KEY)
      return createDefaultDateRange(defaultDate)
    }

    const { storageVersion, storedAtDate, ...storedRange } = parsedValue

    return {
      ...createDefaultDateRange(defaultDate),
      ...storedRange,
      sectorId: parsedValue.sectorId || '',
      groupId: parsedValue.groupId || '',
      activityId: parsedValue.activityId || '',
      blockGroupName: parsedValue.blockGroupName || '',
      deviceGroupName: parsedValue.deviceGroupName || '',
      activityName: parsedValue.activityName || ''
    }
  } catch (error) {
    return createDefaultDateRange(defaultDate)
  }
}

export const writeStoredDashboardDateRange = (range, storage = localStorage, storedAtDate = getDashboardStorageDate()) => {
  try {
    storage?.setItem(DASHBOARD_DATE_RANGE_STORAGE_KEY, JSON.stringify({
      ...range,
      storageVersion: DASHBOARD_DATE_RANGE_STORAGE_VERSION,
      storedAtDate
    }))
  } catch (error) {
    // Ignore storage failures so dashboard filters still work in private or restricted contexts.
  }
}
